/**
 * Open/close state for the jackpot statistics drawer, plus the QA-only win-statistics mock.
 *
 * A separate store on purpose: the drawer is opened from `StandardJackpot`, whose odometer must
 * never re-render, so opening goes through the non-subscribing `openJackpotStatistics` helper
 * and only `JackpotStatisticsHost` subscribes. The mock lives here too (not on the
 * performance-critical `jackpotsStoreSimple` poll) — the feature owns the mock state, the QA
 * panel only calls the actions, mirroring `campaignsStore`.
 */

import { create } from 'zustand';
import { eventTracker } from '../../../core/services/EventTracker';
import type { IJackpotWinsData } from '../types/jackpotStatistics.types';

/**
 * Served while win-statistics mocking is on but the requested jackpot has no generated mock.
 * Frozen and module-level so the drawer's memoised selectors keep a stable reference.
 */
const NO_MOCK_WINS: IJackpotWinsData = Object.freeze({ levels: [] });

interface JackpotStatisticsState {
  isOpen: boolean;
  /** Jackpot instance name the drawer is showing, e.g. 'Bell Link'. */
  jackpotName: string;
  /** Section the drawer was opened from — used for test ids and analytics-friendly markup. */
  sectionName: string;
  /**
   * QA-only master switch. While `true`, {@link useJackpotWins} serves {@link mockWins} and
   * DISABLES the real `/api/jackpot/win/stats` query — exactly like `useCampaignsQuery` gates on
   * `!mockMode`, so a mocked instance name cannot 404-spam the API.
   */
  winsMockMode: boolean;
  /**
   * QA-only. Generated win statistics keyed by jackpot instance name, in the same key space as
   * `jackpotsStoreSimple.jackpotsFromAPI`.
   */
  mockWins: Record<string, IJackpotWinsData>;
  open: (context: { jackpotName: string; sectionName: string }) => void;
  close: () => void;
  setWinsMockMode: (enabled: boolean) => void;
  setMockWins: (jackpotName: string, data: IJackpotWinsData) => void;
  clearMockWins: (jackpotName: string) => void;
  clearAllMockWins: () => void;
}

export const useJackpotStatisticsStore = create<JackpotStatisticsState>()((set, get) => ({
  isOpen: false,
  jackpotName: '',
  sectionName: '',
  winsMockMode: false,
  mockWins: {},
  open: ({ jackpotName, sectionName }) => set({ isOpen: true, jackpotName, sectionName }),
  // Identity fields are kept on close so the exit state stays renderable; only the flag flips.
  close: () => set({ isOpen: false }),

  setWinsMockMode: (enabled) => set({ winsMockMode: enabled }),

  setMockWins: (jackpotName, data) => set({ mockWins: { ...get().mockWins, [jackpotName]: data } }),

  clearMockWins: (jackpotName) => {
    const current = get().mockWins;
    if (!(jackpotName in current)) return;
    // New object rather than `delete` on the live one — subscribers diff by reference.
    const next: Record<string, IJackpotWinsData> = {};
    for (const [name, data] of Object.entries(current)) {
      if (name !== jackpotName) next[name] = data;
    }
    set({ mockWins: next });
  },

  clearAllMockWins: () => {
    if (Object.keys(get().mockWins).length === 0) return;
    set({ mockWins: {} });
  },
}));

/**
 * Imperative open helper. Call this from event handlers — it does not subscribe, so the
 * caller never re-renders because of the drawer.
 */
export const openJackpotStatistics = (context: { jackpotName: string; sectionName: string }): void => {
  const key = (context.sectionName || context.jackpotName).toLowerCase().replace(/\s+/g, '_');
  eventTracker.trackEvent(`jackpot_stats_open_${key}`);
  useJackpotStatisticsStore.getState().open(context);
};

/**
 * The mocked win statistics for one jackpot, or `null` when mocking is off.
 *
 * Returns the stored entry itself (or a frozen empty payload for a jackpot with no mock), so the
 * reference is stable across renders and the drawer's `useMemo` selectors do not recompute.
 */
export const selectMockJackpotWins = (
  state: Pick<JackpotStatisticsState, 'winsMockMode' | 'mockWins'>,
  jackpotName: string,
): IJackpotWinsData | null => (state.winsMockMode ? (state.mockWins[jackpotName] ?? NO_MOCK_WINS) : null);
